import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUpRight,
  Award,
  CheckCircle2,
  Clock,
  Cpu,
  CreditCard,
  Flame,
  Gamepad2,
  Layers,
  QrCode,
  ShieldAlert,
  ShieldCheck,
  Smartphone,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Users,
  Wallet,
  XCircle,
  Zap,
} from "lucide-react";
import { DynamicSubtitle, SubtitleItem } from "./components/DynamicSubtitle";

// 13 Subtitle items synchronized with Whisper word timestamps
const SUBTITLES: SubtitleItem[] = [
  {
    from: 0,
    to: 105,
    text: "Pemain Free Fire yang cuma top up 10 sampai 20 diamond...",
    highlight: "10 sampai 20 diamond",
  },
  {
    from: 105,
    to: 260,
    text: "...sebenernya jauh lebih pinter daripada maksain ratusan ribu demi gengsi!",
    highlight: "jauh lebih pinter",
  },
  {
    from: 260,
    to: 315,
    text: "Coba pikir pakai logika:",
    highlight: "pikir pakai logika",
  },
  {
    from: 315,
    to: 415,
    text: "Ngapain bakar uang banyak buat gacha yang belum tentu dapet...",
    highlight: "bakar uang banyak buat gacha",
  },
  {
    from: 415,
    to: 490,
    text: "...kalo lu bisa main taktis manfaatin spin diskon harian...",
    highlight: "main taktis spin diskon harian",
  },
  {
    from: 490,
    to: 565,
    text: "...atau beli kartu room pas mau by-one doang?",
    highlight: "beli kartu room by-one",
  },
  {
    from: 565,
    to: 640,
    text: "Game itu buat dinikmatin...",
    highlight: "Game itu buat dinikmatin",
  },
  {
    from: 640,
    to: 735,
    text: "...bukan bikin dompet boncos cuma biar keliatan sultan pas mabar!",
    highlight: "bukan bikin dompet boncos",
  },
  {
    from: 735,
    to: 870,
    text: "Dan di Kyu Store, beli recehan 1.000 - 2.000 perak pun gak bakal diremehin!",
    highlight: "1.000 - 2.000 perak",
  },
  {
    from: 870,
    to: 930,
    text: "Cukup tempel ID lu, scan QRIS recehan...",
    highlight: "tempel ID, scan QRIS",
  },
  {
    from: 930,
    to: 1035,
    text: "...detik itu juga diamond langsung masuk ke akun lu!",
    highlight: "detik itu juga diamond masuk",
  },
  {
    from: 1035,
    to: 1160,
    text: "Mulai sekarang stop gengsi, beli secukupnya & main lebih pinter!",
    highlight: "stop gengsi, beli secukupnya",
  },
  {
    from: 1160,
    to: 1310,
    text: "Mau top up receh tetep sat-set? Langsung klik link Kyu Store di bio ya!",
    highlight: "klik link Kyu Store di bio",
  },
];

export const KyuStoreFreeFireUnpopular: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Palette: Neo-Brutalist Streetwear & Swiss Editorial
  const bgMain = "#090F0C"; // Deep Matte Pine
  const paperCream = "#F6F4EE"; // Clean Swiss Editorial Paper
  const acidLime = "#D8FF3E"; // High-Energy Acid Lime
  const signalOrange = "#FF6B35";
  const alertRed = "#FF4B4B";
  const cyanBlue = "#38BDF8";
  const softMuted = "#D6DFD9";
  const darkCard = "#111B15";
  const darkCard2 = "#16231C";
  const brutalShadow = "10px 10px 0px #000000";

  // Calibrated Spring Helper
  const sp = (delay: number, damping = 14, stiffness = 160) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 1 },
    });

  // Global Progress [0 to 1310 frames]
  const globalProgress = interpolate(frame, [0, 1310], [0, 100], {
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgMain,
        color: paperCream,
        fontFamily: "'Rajdhani', sans-serif",
        overflow: "hidden",
      }}
    >
      {/* 1. Primary Mastered Voiceover Track (-16.5 LUFS) */}
      <Audio
        src={staticFile("kyustore_ff_vo_mastered.mp3")}
        volume={1.0}
      />

      {/* 2. Background Music Ducked (-24dB / volume 0.065) */}
      <Audio src={staticFile("bgm-clean.mp3")} volume={0.065} loop />

      {/* 3. Dynamic SFX Cues */}
      {frame === 5 && <Audio src={staticFile("ui-pop.mp3")} volume={0.6} />}
      {frame === 110 && <Audio src={staticFile("swoosh-quick.mp3")} volume={0.5} />}
      {frame === 265 && <Audio src={staticFile("click.mp3")} volume={0.5} />}
      {frame === 320 && <Audio src={staticFile("sfx_error_buzzer.mp3")} volume={0.35} />}
      {frame === 420 && <Audio src={staticFile("chime.mp3")} volume={0.6} />}
      {frame === 575 && <Audio src={staticFile("ui-pop.mp3")} volume={0.5} />}
      {frame === 740 && <Audio src={staticFile("sfx_cash_register.mp3")} volume={0.55} />}
      {frame === 875 && <Audio src={staticFile("ui-success.mp3")} volume={0.65} />}
      {frame === 935 && <Audio src={staticFile("sfx_level_up.mp3")} volume={0.6} />}
      {frame === 1040 && <Audio src={staticFile("chime.mp3")} volume={0.7} />}

      {/* Progress Bar at Top */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 8,
          backgroundColor: "rgba(255, 255, 255, 0.08)",
          zIndex: 999,
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${globalProgress}%`,
            backgroundColor: acidLime,
            boxShadow: `0 0 12px ${acidLime}`,
          }}
        />
      </div>

      {/* Subtle Ambient Glow */}
      <div
        style={{
          position: "absolute",
          top: "20%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 800,
          height: 800,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(216, 255, 62, 0.04) 0%, rgba(0,0,0,0) 70%)",
          pointerEvents: "none",
        }}
      />

      {/* ========================================================================= */}
      {/* SCENE 1: FRAMES 0 - 260 (00:00 - 08.67s) */}
      {/* UNPOPULAR OPINION: TOP UP 10-20 DM JAUH LEBIH PINTER */}
      {/* ========================================================================= */}
      {frame >= 0 && frame < 260 && (
        <div
          style={{
            position: "absolute",
            top: 140,
            bottom: 420,
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: 940,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 22,
            }}
          >
            {/* Top Badge */}
            <div
              style={{
                transform: `scale(${sp(0)})`,
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                backgroundColor: "rgba(216, 255, 62, 0.15)",
                border: `2px solid ${acidLime}`,
                color: acidLime,
                padding: "12px 26px",
                borderRadius: 18,
                fontSize: 24,
                fontWeight: 900,
                letterSpacing: "0.06em",
              }}
            >
              <Flame size={26} color={acidLime} />
              <span>UNPOPULAR OPINION</span>
            </div>

            {/* Main Headline */}
            <h1
              style={{
                fontFamily: "'Anton', sans-serif",
                fontSize: 88,
                lineHeight: 0.95,
                margin: 0,
                color: "#FFFFFF",
                transform: `translateY(${interpolate(sp(5), [0, 1], [40, 0])}px)`,
                opacity: sp(5),
              }}
            >
              TOP UP <span style={{ color: acidLime }}>10-20 DM</span> <br />
              JAUH LEBIH PINTER!
            </h1>

            {/* Comparison Cards: Gengsi vs Taktis */}
            <div
              style={{
                width: "100%",
                display: "flex",
                gap: 18,
                transform: `scale(${sp(15)})`,
              }}
            >
              {/* Card 1: Gengsi Lobby (Kiri) */}
              <div
                style={{
                  flex: 1,
                  backgroundColor: darkCard,
                  borderRadius: 26,
                  border: "3px solid rgba(255, 75, 75, 0.55)",
                  padding: "26px 24px",
                  boxShadow: brutalShadow,
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                  textAlign: "left",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <XCircle size={30} color={alertRed} />
                    <span style={{ fontSize: 24, fontWeight: 900, color: alertRed }}>
                      GENGSI LOBBY
                    </span>
                  </div>
                  <span
                    style={{
                      backgroundColor: alertRed,
                      color: "#FFFFFF",
                      fontSize: 16,
                      fontWeight: 900,
                      padding: "4px 12px",
                      borderRadius: 8,
                    }}
                  >
                    BONCOS
                  </span>
                </div>

                <div style={{ fontSize: 44, fontWeight: 900, color: "#FFFFFF" }}>
                  Rp 100.000+
                </div>
                <div style={{ fontSize: 20, color: softMuted, fontWeight: 700 }}>
                  Dipaksain beli paket gede cuma biar keliatan sultan pas mabar
                </div>
              </div>

              {/* Card 2: Main Taktis (Kanan) */}
              <div
                style={{
                  flex: 1,
                  backgroundColor: darkCard2,
                  borderRadius: 26,
                  border: `3px solid ${acidLime}`,
                  padding: "26px 24px",
                  boxShadow: brutalShadow,
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                  textAlign: "left",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <CheckCircle2 size={30} color={acidLime} />
                    <span style={{ fontSize: 24, fontWeight: 900, color: acidLime }}>
                      MAIN TAKTIS
                    </span>
                  </div>
                  <span
                    style={{
                      backgroundColor: acidLime,
                      color: "#0B120E",
                      fontSize: 16,
                      fontWeight: 900,
                      padding: "4px 12px",
                      borderRadius: 8,
                    }}
                  >
                    CERDAS
                  </span>
                </div>

                <div style={{ fontSize: 44, fontWeight: 900, color: acidLime }}>
                  10 - 20 DM
                </div>
                <div style={{ fontSize: 20, color: paperCream, fontWeight: 700 }}>
                  Cukup buat spin diskon harian & beli kartu room saat butuh!
                </div>
              </div>
            </div>

            {/* Bottom Insight Strip */}
            <div
              style={{
                width: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.45)",
                borderRadius: 22,
                border: "2px dashed rgba(255, 255, 255, 0.22)",
                padding: "20px 28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                transform: `scale(${sp(45)})`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <Zap size={28} color={acidLime} />
                <span style={{ fontSize: 23, fontWeight: 800, color: "#FFFFFF" }}>
                  Gamers sejati itu tau kapan harus hemat & kapan harus keluar uang!
                </span>
              </div>
              <span style={{ fontSize: 21, fontWeight: 900, color: acidLime }}>
                SMART GAMER
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 2: FRAMES 260 - 415 (08.67s - 13.83s) */}
      {/* COBA PIKIR PAKE LOGIKA: NGAPAIN BAKAR UANG BANYAK BUAT GACHA */}
      {/* ========================================================================= */}
      {frame >= 260 && frame < 415 && (
        <div
          style={{
            position: "absolute",
            top: 140,
            bottom: 420,
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: 940,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 26,
            }}
          >
            {/* Warning Tag */}
            <div
              style={{
                transform: `scale(${sp(265)})`,
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                backgroundColor: "rgba(255, 75, 75, 0.15)",
                border: "2px solid #FF4B4B",
                color: "#FF4B4B",
                padding: "12px 26px",
                borderRadius: 18,
                fontSize: 24,
                fontWeight: 900,
                letterSpacing: "0.04em",
              }}
            >
              <ShieldAlert size={26} color="#FF4B4B" />
              <span>LOGIKA REALISTIS</span>
            </div>

            {/* Display Headline */}
            <h1
              style={{
                fontFamily: "'Anton', sans-serif",
                fontSize: 90,
                lineHeight: 0.95,
                margin: 0,
                color: "#FFFFFF",
                transform: `translateY(${interpolate(sp(270), [0, 1], [40, 0])}px)`,
                opacity: sp(270),
              }}
            >
              NGAPAIN <span style={{ color: alertRed }}>BAKAR UANG</span> <br />
              BUAT GACHA ZONK?
            </h1>

            {/* Reality Breakdown Card */}
            <div
              style={{
                width: "100%",
                backgroundColor: darkCard,
                borderRadius: 28,
                border: "3px solid rgba(255, 75, 75, 0.55)",
                padding: "36px 40px",
                boxShadow: brutalShadow,
                transform: `scale(${sp(280)})`,
                display: "flex",
                flexDirection: "column",
                gap: 24,
                textAlign: "left",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                  <TrendingDown size={48} color={alertRed} />
                  <div>
                    <div style={{ fontSize: 32, fontWeight: 900, color: "#FFFFFF" }}>
                      GACHA PENUH ITU SKEMA RUGI
                    </div>
                    <div style={{ fontSize: 23, color: softMuted, fontWeight: 700 }}>
                      Beli ratusan diamond, spin puluhan kali, dapetnya trial 3 hari
                    </div>
                  </div>
                </div>
              </div>

              {/* Contrast Points */}
              <div
                style={{
                  backgroundColor: "rgba(0, 0, 0, 0.45)",
                  borderRadius: 22,
                  padding: "26px 32px",
                  border: "2px dashed rgba(255, 75, 75, 0.45)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    fontSize: 25,
                    fontWeight: 800,
                  }}
                >
                  <XCircle size={30} color={alertRed} />
                  <span style={{ color: "#FFFFFF" }}>
                    Bakar ratusan ribu sekali spin = Dompet langsung kering
                  </span>
                </div>
                <div
                  style={{
                    height: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.12)",
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    fontSize: 25,
                    fontWeight: 800,
                  }}
                >
                  <CheckCircle2 size={30} color={acidLime} />
                  <span style={{ color: acidLime }}>
                    Beli receh pas ada diskon harian = Peluang dapet jauh lebih hemat!
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 3: FRAMES 415 - 570 (13.83s - 19.00s) */}
      {/* MAIN TAKTIS: SPIN DISKON HARIAN & KARTU ROOM BY ONE */}
      {/* ========================================================================= */}
      {frame >= 415 && frame < 570 && (
        <div
          style={{
            position: "absolute",
            top: 140,
            bottom: 420,
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: 940,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 22,
            }}
          >
            {/* Header Badge */}
            <div
              style={{
                transform: `scale(${sp(420)})`,
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                backgroundColor: "rgba(56, 189, 248, 0.15)",
                border: `2px solid ${cyanBlue}`,
                color: cyanBlue,
                padding: "12px 26px",
                borderRadius: 18,
                fontSize: 24,
                fontWeight: 900,
                letterSpacing: "0.06em",
              }}
            >
              <Gamepad2 size={26} color={cyanBlue} />
              <span>STRATEGI TAKTIS FREE FIRE</span>
            </div>

            {/* Display Headline */}
            <h1
              style={{
                fontFamily: "'Anton', sans-serif",
                fontSize: 88,
                lineHeight: 0.95,
                margin: 0,
                color: "#FFFFFF",
                transform: `translateY(${interpolate(sp(425), [0, 1], [40, 0])}px)`,
                opacity: sp(425),
              }}
            >
              SPIN DISKON 9 DM & <br />
              <span style={{ color: acidLime }}>KARTU ROOM BY-ONE!</span>
            </h1>

            {/* 2 Bento Cards for Real Tactics */}
            <div style={{ width: "100%", display: "flex", gap: 18 }}>
              {/* Card 1: Spin 9 DM */}
              <div
                style={{
                  flex: 1,
                  backgroundColor: darkCard,
                  borderRadius: 26,
                  border: `3px solid ${acidLime}`,
                  padding: "28px 26px",
                  boxShadow: brutalShadow,
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                  textAlign: "left",
                  transform: `scale(${sp(435)})`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontSize: 22, fontWeight: 900, color: acidLime }}>
                    SPIN HARIAN
                  </span>
                  <span
                    style={{
                      backgroundColor: acidLime,
                      color: "#0B120E",
                      fontSize: 16,
                      fontWeight: 900,
                      padding: "4px 12px",
                      borderRadius: 8,
                    }}
                  >
                    DISCOUNT
                  </span>
                </div>
                <div style={{ fontSize: 52, fontWeight: 900, color: "#FFFFFF" }}>
                  9 - 19 DM
                </div>
                <div style={{ fontSize: 20, color: softMuted, fontWeight: 700 }}>
                  Tiap hari manfaatin diskon putaran pertama, hoki dapet bundle/senjata
                  tanpa boncos!
                </div>
              </div>

              {/* Card 2: Custom Room Card */}
              <div
                style={{
                  flex: 1,
                  backgroundColor: darkCard2,
                  borderRadius: 26,
                  border: `3px solid ${cyanBlue}`,
                  padding: "28px 26px",
                  boxShadow: brutalShadow,
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                  textAlign: "left",
                  transform: `scale(${sp(445)})`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontSize: 22, fontWeight: 900, color: cyanBlue }}>
                    KARTU CUSTOM ROOM
                  </span>
                  <span
                    style={{
                      backgroundColor: cyanBlue,
                      color: "#0B120E",
                      fontSize: 16,
                      fontWeight: 900,
                      padding: "4px 12px",
                      borderRadius: 8,
                    }}
                  >
                    BY-ONE
                  </span>
                </div>
                <div style={{ fontSize: 52, fontWeight: 900, color: cyanBlue }}>
                  50 DM
                </div>
                <div style={{ fontSize: 20, color: paperCream, fontWeight: 700 }}>
                  Adu mekanik di tongkrongan pas mau tanding, beli seperlunya aja!
                </div>
              </div>
            </div>

            {/* Bottom Info */}
            <div
              style={{
                width: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.45)",
                borderRadius: 22,
                border: "2px dashed rgba(255, 255, 255, 0.22)",
                padding: "18px 26px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 14,
                transform: `scale(${sp(460)})`,
              }}
            >
              <Sparkles size={24} color={acidLime} />
              <span style={{ fontSize: 22, fontWeight: 800, color: "#FFFFFF" }}>
                Kebutuhan lu itu spesifik receh, ngapain dipaksa beli paket ratusan ribu?
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 4: FRAMES 570 - 735 (19.00s - 24.50s) */}
      {/* GAME BUAT DINIKMATIN BUKAN BIKIN DOMPET BONCOS */}
      {/* ========================================================================= */}
      {frame >= 570 && frame < 735 && (
        <div
          style={{
            position: "absolute",
            top: 140,
            bottom: 420,
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: 940,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 24,
            }}
          >
            {/* Tag */}
            <div
              style={{
                transform: `scale(${sp(575)})`,
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                backgroundColor: "rgba(255, 107, 53, 0.15)",
                border: "2px solid #FF6B35",
                color: "#FF6B35",
                padding: "12px 26px",
                borderRadius: 18,
                fontSize: 24,
                fontWeight: 900,
                letterSpacing: "0.04em",
              }}
            >
              <Users size={26} color="#FF6B35" />
              <span>REALITA MABAR TONGKRONGAN</span>
            </div>

            {/* Display Headline */}
            <h1
              style={{
                fontFamily: "'Anton', sans-serif",
                fontSize: 88,
                lineHeight: 0.95,
                margin: 0,
                color: "#FFFFFF",
                transform: `translateY(${interpolate(sp(580), [0, 1], [40, 0])}px)`,
                opacity: sp(580),
              }}
            >
              GAME BUAT DINIKMATIN <br />
              <span style={{ color: acidLime }}>BUKAN BIKIN BONCOS!</span>
            </h1>

            {/* Comparison Box */}
            <div
              style={{
                width: "100%",
                backgroundColor: darkCard,
                borderRadius: 28,
                border: "3px solid rgba(255, 255, 255, 0.18)",
                padding: "36px 40px",
                boxShadow: brutalShadow,
                transform: `scale(${sp(590)})`,
                display: "flex",
                flexDirection: "column",
                gap: 22,
                textAlign: "left",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "2px solid rgba(255, 255, 255, 0.12)",
                  paddingBottom: 18,
                }}
              >
                <span style={{ fontSize: 26, fontWeight: 900, color: "#FFFFFF" }}>
                  SKILL LEBIH PENTING DARI GENGSI
                </span>
                <span
                  style={{
                    backgroundColor: acidLime,
                    color: "#0B120E",
                    fontSize: 18,
                    fontWeight: 900,
                    padding: "6px 16px",
                    borderRadius: 10,
                  }}
                >
                  FAKTA LAPANGAN
                </span>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    fontSize: 25,
                    fontWeight: 800,
                    color: softMuted,
                  }}
                >
                  <XCircle size={32} color={alertRed} />
                  <span>Keliatan sultan di lobby tapi pas perang knock pertama kali</span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    fontSize: 25,
                    fontWeight: 800,
                    color: "#FFFFFF",
                  }}
                >
                  <CheckCircle2 size={32} color={acidLime} />
                  <span>Set outfit simpel hasil spin cerdas, tapi gameplay gendong tim</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 5: FRAMES 735 - 1035 (24.50s - 34.50s) */}
      {/* DI KYU STORE: BELI RECEHAN 1.000 - 2.000 PERAK TETEP DILAYANI */}
      {/* ========================================================================= */}
      {frame >= 735 && frame < 1035 && (
        <div
          style={{
            position: "absolute",
            top: 140,
            bottom: 420,
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: 940,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 22,
            }}
          >
            {/* Brand Pill */}
            <div
              style={{
                transform: `scale(${sp(740)})`,
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                backgroundColor: "rgba(216, 255, 62, 0.15)",
                border: `2px solid ${acidLime}`,
                color: acidLime,
                padding: "12px 26px",
                borderRadius: 18,
                fontSize: 24,
                fontWeight: 900,
                letterSpacing: "0.06em",
              }}
            >
              <Zap size={26} color={acidLime} />
              <span>KYU STORE · ZERO MINIMUM TOP UP</span>
            </div>

            {/* Display Headline */}
            <h1
              style={{
                fontFamily: "'Anton', sans-serif",
                fontSize: 88,
                lineHeight: 0.95,
                margin: 0,
                color: "#FFFFFF",
                transform: `translateY(${interpolate(sp(745), [0, 1], [40, 0])}px)`,
                opacity: sp(745),
              }}
            >
              BELI <span style={{ color: acidLime }}>1.000 - 2.000 PERAK</span> <br />
              TETEP DILAYANI SAT-SET!
            </h1>

            {/* 3 Step Process Card */}
            <div
              style={{
                width: "100%",
                display: "flex",
                gap: 16,
                transform: `scale(${sp(755)})`,
              }}
            >
              {[
                {
                  step: "01",
                  title: "TEMPEL ID",
                  desc: "Cukup masukin User ID akun FF lu",
                  icon: Smartphone,
                  color: "#FFFFFF",
                },
                {
                  step: "02",
                  title: "SCAN QRIS",
                  desc: "Bayar seribu perak pake e-wallet apa aja",
                  icon: QrCode,
                  color: acidLime,
                },
                {
                  step: "03",
                  title: "LANGSUNG MASUK",
                  desc: "Detik itu juga diamond landing di akun",
                  icon: Zap,
                  color: cyanBlue,
                },
              ].map((item, idx) => {
                const IconComp = item.icon;
                return (
                  <div
                    key={idx}
                    style={{
                      flex: 1,
                      backgroundColor: darkCard,
                      borderRadius: 24,
                      border: `2px solid ${item.color}`,
                      padding: "26px 18px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 12,
                      boxShadow: brutalShadow,
                    }}
                  >
                    <div
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: 18,
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconComp size={36} color={item.color} />
                    </div>
                    <span
                      style={{ fontSize: 24, fontWeight: 900, color: item.color }}
                    >
                      {item.title}
                    </span>
                    <span
                      style={{ fontSize: 18, fontWeight: 700, color: softMuted }}
                    >
                      {item.desc}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Zero Minimum Guarantee */}
            <div
              style={{
                width: "100%",
                backgroundColor: darkCard2,
                borderRadius: 22,
                border: "2px solid rgba(255, 255, 255, 0.18)",
                padding: "20px 28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                transform: `scale(${sp(820)})`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <CheckCircle2 size={28} color={acidLime} />
                <span style={{ fontSize: 24, fontWeight: 800, color: "#FFFFFF" }}>
                  Gak ada batas minimal ribet · Bebas biaya admin siluman
                </span>
              </div>
              <span style={{ fontSize: 22, fontWeight: 900, color: acidLime }}>
                100% LEGAL
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 6: FRAMES 1035 - 1310 (34.50s - 43.60s) */}
      {/* CALL TO ACTION: STOP GENGSI, KLIK LINK KYU STORE DI BIO */}
      {/* ========================================================================= */}
      {frame >= 1035 && (
        <div
          style={{
            position: "absolute",
            top: 140,
            bottom: 420,
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: 940,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 24,
            }}
          >
            {/* CTA Badge */}
            <div
              style={{
                transform: `scale(${sp(1040)})`,
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                backgroundColor: acidLime,
                color: "#0B120E",
                padding: "14px 28px",
                borderRadius: 18,
                fontSize: 24,
                fontWeight: 900,
                letterSpacing: "0.05em",
                boxShadow: "5px 5px 0px #000000",
              }}
            >
              <Flame size={28} color="#0B120E" />
              <span>MAIN LEBIH PINTER SEKARANG</span>
            </div>

            {/* Display Headline */}
            <h1
              style={{
                fontFamily: "'Anton', sans-serif",
                fontSize: 90,
                lineHeight: 0.95,
                margin: 0,
                color: "#FFFFFF",
                transform: `translateY(${interpolate(sp(1045), [0, 1], [40, 0])}px)`,
                opacity: sp(1045),
              }}
            >
              TOP UP RECEH SAT-SET <br />
              <span style={{ color: acidLime }}>KLIK LINK DI BIO!</span>
            </h1>

            {/* Giant Action Box */}
            <div
              style={{
                width: "100%",
                backgroundColor: acidLime,
                color: "#0B120E",
                borderRadius: 34,
                padding: "40px 42px",
                boxShadow: "12px 12px 0px #000000",
                transform: `scale(${sp(1055)})`,
                display: "flex",
                flexDirection: "column",
                gap: 26,
                border: "4px solid #000000",
                textAlign: "left",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                  <div
                    style={{
                      width: 78,
                      height: 78,
                      borderRadius: 22,
                      backgroundColor: "#0B120E",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Gamepad2 size={46} color={acidLime} />
                  </div>
                  <div>
                    <div style={{ fontSize: 38, fontWeight: 900 }}>
                      KYU STORE FREE FIRE
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: "#18251F" }}>
                      Top Up Receh Bebas Gengsi · Scan QRIS Masuk Instan!
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    backgroundColor: "#0B120E",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ArrowUpRight size={42} color={acidLime} />
                </div>
              </div>

              {/* Bottom Guarantee Banner */}
              <div
                style={{
                  backgroundColor: "#0B120E",
                  color: "#FFFFFF",
                  borderRadius: 20,
                  padding: "20px 28px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <CheckCircle2 size={26} color={acidLime} />
                  <span style={{ fontSize: 24, fontWeight: 800 }}>
                    Mulai Dari 1.000 Perak
                  </span>
                </div>
                <span style={{ fontSize: 24, fontWeight: 800, color: acidLime }}>
                  Otomatis 24 Jam
                </span>
              </div>
            </div>

            {/* Profile Link Reminder */}
            <div
              style={{
                width: "100%",
                backgroundColor: darkCard,
                borderRadius: 22,
                border: "2px solid rgba(255, 255, 255, 0.18)",
                padding: "20px 28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 14,
                transform: `scale(${sp(1140)})`,
              }}
            >
              <ArrowUpRight size={28} color={acidLime} />
              <span style={{ fontSize: 24, fontWeight: 800, color: "#FFFFFF" }}>
                Tinggal buka profil dan klik link Kyu Store di bio ya!
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 4. DYNAMIC SUBTITLE ENGINE (Y: 1540px / bottom: 380px SAFE BAND) */}
      {/* ========================================================================= */}
      <DynamicSubtitle
        frame={frame}
        subtitles={SUBTITLES}
        bottom={380} // Perfectly anchored above TikTok bottom UI
        highlightColor={acidLime}
        backgroundColor="rgba(11, 18, 14, 0.96)"
        fontSize={32}
      />

      {/* 5. Clean Watermark Below Subtitle */}
      <div
        style={{
          position: "absolute",
          bottom: 310,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 90,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            backgroundColor: "rgba(11, 18, 14, 0.65)",
            padding: "8px 24px",
            borderRadius: 20,
            border: "1px solid rgba(255, 255, 255, 0.14)",
          }}
        >
          <span
            style={{
              fontFamily: "'Rajdhani', sans-serif",
              fontSize: 24,
              fontWeight: 800,
              letterSpacing: "0.08em",
              color: "rgba(255, 255, 255, 0.7)",
            }}
          >
            TikTok @kyuidn.co
          </span>
        </div>
      </div>
    </AbsoluteFill>
  );
};
